import { ConfirmDialog } from '@/components/dialog/confirm-dialog';
import { useAuth } from '@/contexts/auth-context';
import {
  useRemoveAffiliateMembership,
  type RemoveAffiliateVariables,
} from '@/hooks/actions/use-remove-affiliate-membership';
import { Button } from '@chakra-ui/react';
import type { AxiosError } from 'axios';
import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useConfirmationEscape } from '@/hooks/use-confirmation-escape';
import { useTranslation } from 'react-i18next';

interface AffiliationRemovalActionProps {
  userId: string;
  programId: string;
  programName: string;
  affiliateName: string;
  lifecycleRevision: number;
  onRemoved: () => void;
  renderTrigger?: (requestRemoval: () => void, pending: boolean) => ReactNode;
  initiallyOpen?: boolean;
  onDismiss?: () => void;
  finalFocusEl?: () => HTMLElement | null;
}

interface ReviewedMembership {
  contextKey: string;
  variables: RemoveAffiliateVariables;
  programName: string;
  affiliateName: string;
}

/** Capture the reviewed episode once, keeping the same operation ID on transport retry. */
export default function AffiliationRemovalAction({
  userId,
  programId,
  programName,
  affiliateName,
  lifecycleRevision,
  onRemoved,
  renderTrigger,
  initiallyOpen = false,
  onDismiss,
  finalFocusEl,
}: AffiliationRemovalActionProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const removal = useRemoveAffiliateMembership();
  const contextKey = `${user?.currentBrandId}:${userId}:${programId}`;
  const createReview = (): ReviewedMembership | null =>
    user?.currentBrandId
      ? {
          contextKey,
          programName,
          affiliateName,
          variables: {
            userId,
            programId,
            brandId: user.currentBrandId,
            expectedLifecycleRevision: lifecycleRevision,
            operationId: crypto.randomUUID(),
          },
        }
      : null;
  const [reviewed, setReviewed] = useState<ReviewedMembership | null>(() =>
    initiallyOpen ? createReview() : null
  );
  const currentReview = reviewed?.contextKey === contextKey ? reviewed : null;
  const activeReview = useRef<ReviewedMembership | null>(null);

  useLayoutEffect(() => {
    // Cached URL navigation can reuse this mounted component. A different
    // membership needs a fresh gesture, while same-context rereads keep retry identity.
    setReviewed((current) => (current?.contextKey === contextKey ? current : null));
  }, [contextKey]);

  useLayoutEffect(() => {
    activeReview.current = currentReview;
    return () => {
      activeReview.current = null;
    };
  }, [currentReview]);

  const dismiss = useCallback(() => {
    if (removal.isPending) return;
    setReviewed(null);
    onDismiss?.();
  }, [removal.isPending, onDismiss]);
  useConfirmationEscape(currentReview !== null, removal.isPending, dismiss);

  const confirm = async () => {
    if (!currentReview || removal.isPending) return;
    try {
      await removal.mutateAsync(currentReview.variables);
      if (activeReview.current !== currentReview) return;
      setReviewed(null);
      onRemoved();
    } catch (error) {
      if (activeReview.current !== currentReview) return;
      const status = (error as AxiosError).response?.status;
      // A stale review requires another explicit gesture after the server reread.
      if (status === 409 || status === 403) dismiss();
    }
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger(() => setReviewed(createReview()), removal.isPending)
      ) : (
        <Button
          variant="outline"
          colorPalette="red"
          disabled={!user?.currentBrandId}
          onClick={() => setReviewed(createReview())}
        >
          {t('affiliates.removal.action')}
        </Button>
      )}
      <ConfirmDialog
        isOpen={currentReview !== null}
        title={t('affiliates.removal.title')}
        description={t('affiliates.removal.description', {
          programName: currentReview?.programName ?? programName,
          affiliateName: currentReview?.affiliateName ?? affiliateName,
        })}
        confirmText={t('affiliates.removal.action')}
        confirmColorPalette="red"
        isLoading={removal.isPending}
        onConfirm={() => void confirm()}
        onCancel={dismiss}
        finalFocusEl={finalFocusEl}
      />
    </>
  );
}
